import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
export function Button({ variant = 'secondary', className, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 min-h-11 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed';
  const v = {
    primary: 'bg-accent text-white hover:bg-accent-ink',
    secondary: 'bg-surface border border-rule text-ink hover:bg-accent-soft',
    ghost: 'text-ink-2 hover:bg-accent-soft',
    danger: 'bg-danger-soft text-danger hover:brightness-95',
  }[variant];
  return <button className={cx(base, v, className)} {...p} />;
}

export function Field({ label, hint, error, children, required }: { label: string; hint?: string; error?: string | null; children: ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-ink-2">{label}{required && <span className="text-danger"> *</span>}</span>
      {children}
      {error ? <span className="mt-1 block text-sm text-danger">{error}</span> : hint ? <span className="mt-1 block text-sm text-muted">{hint}</span> : null}
    </label>
  );
}

const control = 'w-full rounded-lg border border-rule bg-surface px-3 py-2.5 min-h-11 text-ink placeholder:text-muted';
export function Input({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) { return <input className={cx(control, className)} {...p} />; }
export function Select({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) { return <select className={cx(control, className)} {...p} />; }
export function Textarea({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) { return <textarea className={cx(control, 'min-h-24', className)} {...p} />; }

export function Pill({ tone = 'neutral', children }: { tone?: 'neutral' | 'accent' | 'warn' | 'danger'; children: ReactNode }) {
  const t = { neutral: 'bg-rule/60 text-ink-2', accent: 'bg-accent-soft text-accent-ink', warn: 'bg-warn-soft text-warn', danger: 'bg-danger-soft text-danger' }[tone];
  return <span className={cx('inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide', t)}>{children}</span>;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-xl border border-rule bg-surface p-4 shadow-[0_1px_2px_rgba(20,40,30,.06)]', className)}>{children}</div>;
}

export function Modal({ open, title, onClose, children, wide }: { open: boolean; title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className={cx('max-h-[95vh] w-full overflow-y-auto rounded-t-2xl bg-bg p-5 shadow-xl sm:rounded-2xl', wide ? 'sm:max-w-3xl' : 'sm:max-w-lg')} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{title}</h2>
          <button className="rounded-lg p-2 text-ink-2 hover:bg-accent-soft" onClick={onClose} aria-label="close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-dashed border-rule p-8 text-center text-muted">{children}</div>;
}

export { cx };
